import _ from 'lodash';
import qs from 'query-string';
import React from 'react';

import Alert from 'components/Alert';
import {
  getStepIndex,
  handleStepContinue,
  handleStepPrevious
} from 'components/Wizard/helpers';
import {
  createArtistMembersStep,
  createArtistNameStep,
  createDspConnectionStep
} from 'components/Wizard/stepCreators';
import {
  DIGITAL_SERVICE_PROVIDERS,
  DSP_CONNECT_ROLES,
  DSP_NAMES_FORMATTED,
  PROMO_ACCESS
} from 'config/constants';
import history from 'config/history';
import routeMap from 'config/routes';
import { canLabelAccessPromoPool, consoleDev } from 'helpers';
import { addSubscriberToLabel } from 'redux/actions/labelSubscribers';
import { showAlert } from 'redux/actions/ui';
const WIZARD_OVERVIEW_TITLE = 'Artist Profile Setup';

export const ARTIST_WIZARD_CONTEXTS = {
  GUEST_DJ: 'guest-dj',
  PROMO_POOL: 'promo-pool',
  SOUNDCLOUD_CONNECT: 'soundcloud-connect',
  SPOTIFY_CONNECT: 'spotify-connect',
  APPLE_MUSIC_CONNECT: 'apple-music-connect',
  LABEL_RELEASE: 'label-release'
};

const {
  GUEST_DJ,
  PROMO_POOL,
  SOUNDCLOUD_CONNECT,
  SPOTIFY_CONNECT,
  APPLE_MUSIC_CONNECT,
  LABEL_RELEASE
} = ARTIST_WIZARD_CONTEXTS;
const { SOUNDCLOUD, SPOTIFY, APPLE_MUSIC } = DIGITAL_SERVICE_PROVIDERS;

const serviceByContext = {
  [SOUNDCLOUD_CONNECT]: SOUNDCLOUD,
  [APPLE_MUSIC_CONNECT]: APPLE_MUSIC,
  [SPOTIFY_CONNECT]: SPOTIFY
};

/**
 * [getWizardPropsForContext]
 *
 * @param {object} [artistProfile] - artist profile if wizard is for an existing proton artist. It's possible
 * for a context to be created without an artist profile (i.e. in the process of creating a new artist). This
 * function is rerun when the artist profile is created / updated, so later steps reliant on the data should have it.
 * @param {string} [context] - determines which pre-defined context of the wizard to get props for
 * @param {string} [stepId] - undefined if single step wizard
 * @param {func} dispatch - redux dispatch function
 * @param {object} user - current user object
 */

export const getWizardPropsForContext = ({
  artistProfile,
  context,
  stepId,
  dispatch,
  user,
  label,
  isAddAnyPromoArtistActive
}) => {
  const artistData = artistProfile || {}; // safely reference nested artist profile data even if undefined
  const commonProps = { context, stepId, overviewTitle: WIZARD_OVERVIEW_TITLE };
  const overviewPath = routeMap.artistProfileWizard.getOverviewPathForContext(context);

  switch (context) {
    case GUEST_DJ: {
      const artistNameStep = createArtistNameStep({ artistProfile, context });
      const connectSoundCloudStep = createDspConnectionStep({
        service: SOUNDCLOUD,
        context,
        artistProfile
      });

      const steps = [artistNameStep, connectSoundCloudStep];
      const stepIndex = getStepIndex(steps, stepId);

      const goToConfirmation = () =>
        history.push(
          { pathname: routeMap.confirmation, search: history.location.search },
          {
            title: "Great! You're good to go.",
            message:
              `${artistData.name}'s profile on Proton is ready. ` +
              'You can return to SoundSystem and add them to your radio show.'
          }
        );

      return {
        ...commonProps,
        backText: 'Back to SoundSystem',
        backPath: 'https://soundsystem.protonradio.com',
        overviewSubtitle:
          "We'll help you setup a new artist profile on Proton to feature on your radio show!",
        steps,
        stepIndex,
        onFinish: goToConfirmation,
        handleContinue: onContinue =>
          handleStepContinue({
            onContinue,
            onFinish: goToConfirmation,
            steps,
            stepIndex
          }),
        handlePrevious: onPrevious =>
          handleStepPrevious({ steps, stepIndex, overviewPath, onPrevious })
      };
    }

    case PROMO_POOL: {
      const connectSoundCloudStep = createDspConnectionStep({
        service: SOUNDCLOUD,
        context,
        artistProfile
      });

      const artistNameStep = createArtistNameStep({ artistProfile, context });
      const artistMembersStep = createArtistMembersStep({
        artistProfile,
        context,
        user
      });

      const labelHasFullPromoAccess = canLabelAccessPromoPool(label, PROMO_ACCESS.FULL);

      const steps = isAddAnyPromoArtistActive
        ? [
            labelHasFullPromoAccess && artistNameStep,
            artistMembersStep,
            connectSoundCloudStep
          ].filter(Boolean)
        : [connectSoundCloudStep];
      const stepIndex = getStepIndex(steps, stepId);

      const onFinish = (options = {}) => {
        const { onError } = options;
        // 1) Add user to promo subscribers
        // 2) if success, display success message in alert and navigate to label subscribers page

        return dispatch(addSubscriberToLabel(label.id, artistData.id))
          .then(() => {
            dispatch(
              showAlert({
                message:
                  `${artistData.name} has been invited to ${label.name}` + ' promo pool.',
                type: Alert.TYPES.MESSAGE
              })
            );

            history.push(`/labels/${label.id}/${_.kebabCase(label.name)}/subscribers`);
          })
          .catch(e => {
            if (onError) onError();
            dispatch(showAlert({ error: e }));
          });
      };

      return {
        ...commonProps,
        overviewSubtitle:
          "We'll help you setup a new artist profile on Proton for your promo pool!",
        backText: 'Back to Subscribers',
        // NOTE: Since dynamic url, 'backPath' is passed via ?backPath url param
        // backPath: /labels/:labelId/subscribers
        steps,
        stepIndex,
        onFinish,
        handleContinue: onContinue =>
          handleStepContinue({
            onContinue,
            onFinish,
            steps,
            stepIndex
          }),
        handlePrevious: onPrevious =>
          handleStepPrevious({ steps, stepIndex, overviewPath, onPrevious })
      };
    }

    case LABEL_RELEASE: {
      const artistNameStep = createArtistNameStep({ artistProfile, context });
      const artistMembersStep = createArtistMembersStep({
        artistProfile,
        context,
        user
      });
      const [spotifyConnectStep, appleConnectStep] = [SPOTIFY, APPLE_MUSIC].map(service =>
        createDspConnectionStep({
          service,
          context,
          artistProfile
        })
      );

      const steps = [
        artistNameStep,
        artistMembersStep,
        spotifyConnectStep,
        appleConnectStep
      ];
      const stepIndex = getStepIndex(steps, stepId);

      const goToConfirmation = () =>
        history.push(
          { pathname: routeMap.confirmation, search: history.location.search },
          {
            title: "Great! You're good to go.",
            message:
              `${artistData.name}'s profile on Proton is ready. ` +
              'You can return to SoundSystem and add them to your release.'
          }
        );

      return {
        ...commonProps,
        backText: 'Back to SoundSystem',
        backPath: 'https://soundsystem.protonradio.com',
        overviewSubtitle: (
          <>
            We&apos;ll help you set-up a new artist profile on Proton! If your
            artist&apos;s profile already exists on other platforms, don&apos;t worry:
            you&apos;re just setting up their profile inside Proton. (
            <a href={routeMap.external.help.newArtistProfile}>Learn more</a>)
          </>
        ),
        steps: steps,
        stepIndex,
        onFinish: goToConfirmation,
        handleContinue: onContinue =>
          handleStepContinue({
            onContinue,
            onFinish: goToConfirmation,
            steps,
            stepIndex
          }),
        handlePrevious: onPrevious =>
          handleStepPrevious({ steps, stepIndex, overviewPath, onPrevious })
      };
    }

    case SOUNDCLOUD_CONNECT:
    case APPLE_MUSIC_CONNECT:
    case SPOTIFY_CONNECT: {
      const service = serviceByContext[context];

      const connectDspStep = createDspConnectionStep({
        service,
        context,
        artistProfile,
        singleStep: true
      });

      const { role, finishPath } = qs.parse(history.location.search);
      const isSSLabelManager = role === DSP_CONNECT_ROLES.SS_LABEL_MANAGER;

      const getSettingsUrl = artistSlug =>
        artistSlug ? `${artistSlug}` + routeMap.artists.settings.root : null;

      const onFinish = artistProfile => {
        const artistData = artistProfile || artistData;
        // If coming to this page from SS, we want to go to a confirmation page,
        // otherwise visit the artist profile editor
        if (isSSLabelManager) {
          history.push(routeMap.confirmation, {
            title: 'Great!',
            message:
              `${artistData.name}'s profile has been successfully updated. ` +
              'If you were preparing a release in SoundSystem, ' +
              'please go back & try adding them to the track again.'
          });
          return;
        }

        dispatch(
          showAlert({
            message: `${artistData.name}'s ${DSP_NAMES_FORMATTED[service]} profile has been connected.`,
            type: Alert.TYPES.MESSAGE,
            timeout: 3000
          })
        );

        // NOTE: If coming from pro user verification, we want to go back to that page (included in 'finishPath')
        if (finishPath) {
          return history.push(finishPath);
        }

        history.push(
          getSettingsUrl(artistData.slug) + routeMap.artists.settings.connections
        );
      };

      return {
        ...commonProps,
        backText: isSSLabelManager ? 'Back to SoundSystem' : 'Back to Artist Settings',
        backPath: isSSLabelManager
          ? 'https://soundsystem.protonradio.com'
          : getSettingsUrl(artistData.slug),
        steps: [connectDspStep],
        handleContinue: onContinue =>
          onContinue()
            .then(artistProfile => {
              onFinish(artistProfile);
            })
            .catch(e => {
              consoleDev(e);
            })
      };
    }

    default:
      throw new Error('Missing or unrecognized context');
  }
};
